import { IMG_BILL_BACKGROUND, IMG_REFUND_COMPLETED } from '@repo/assets';
import { addComma } from '@repo/utils';
import { format } from 'date-fns';
import { ReactBarcode } from 'react-jsbarcode';
import styled from 'styled-components';

type Props = {
  storeName: string;
  price: number;
  refund: number;
  serialNumber?: string;
  saleDate: string;
  refundDate?: string;
  onClick?: () => void;
  nation?: string;
  isMultiple?: boolean;
};

export const RefundBill = ({
  storeName,
  price,
  refund,
  serialNumber,
  saleDate,
  refundDate,
  nation = '',
  onClick = () => {},
  isMultiple = false,
}: Props) => {
  const isCompleted = !!refundDate;

  const nationIndex = nation === 'KOR' ? 1 : 0;
  const text = {
    purchaseDate: ['Date of Purchase', '구매일자'],
    payment: ['Payment', '결제금액'],
    refundAmount: ['Amount of Refund', '환급액'],
    receiptDetail: ['Receipt Detail', '영수증 상세보기'],
  };

  return (
    <Section
      isCompleted={isCompleted}
      isMultiple={isMultiple}
      onClick={onClick}
    >
      <TopWrapper>
        <PurchaseDate>
          {saleDate ? format(new Date(saleDate), 'yyyy.MM.dd') : '-'}
        </PurchaseDate>
        <StoreName>{storeName}</StoreName>
        <InfoList>
          <InfoItem>
            <Dt>{text.payment[nationIndex]}</Dt>
            <Dd>₩ {addComma(price)}</Dd>
          </InfoItem>
          <InfoItem>
            <Dt>{text.refundAmount[nationIndex]}</Dt>
            <Dd>₩ {addComma(refund)}</Dd>
          </InfoItem>
          {isCompleted && (
            <CompletedImage src={IMG_REFUND_COMPLETED} alt='환급 완료' />
          )}
        </InfoList>
      </TopWrapper>
      {serialNumber ? (
        <Barcode>
          <BarcodeImage
            value={serialNumber}
            options={{ format: 'CODE39', displayValue: false, width: 1 }}
          />
        </Barcode>
      ) : (
        <ReceiptDetailButton>
          {text.receiptDetail[nationIndex]}
        </ReceiptDetailButton>
      )}
    </Section>
  );
};

const Section = styled.div<{ isCompleted: boolean; isMultiple: boolean }>`
  position: relative;
  min-width: 200px;
  width: 90%;
  height: auto;
  padding: 28px 24px;
  overflow: visible;
  background-color: transparent;
  z-index: 0;
  filter: drop-shadow(0px 0px 10px rgba(174, 191, 204, 0.5));
  margin: 24px ${({ isMultiple }) => (isMultiple ? '0' : 'auto')};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;

  &::before {
    width: 100%;
    height: 100%;
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-image: url(${IMG_BILL_BACKGROUND});
    background-size: 100% 100%;
    background-repeat: no-repeat;
    z-index: -1;
  }
`;

const TopWrapper = styled.div`
  width: 100%;
`;

const PurchaseDate = styled.p`
  color: var(--KTP-Grayscale-100, var(--KTP-Color-Mono-100, #030303));
  font-size: 14px;
  font-weight: 500;
  line-height: 150%; /* 21px */
  letter-spacing: -0.28px;
`;

const StoreName = styled.h3`
  color: var(--KTP-Grayscale-100, var(--KTP-Color-Mono-100, #030303));
  font-size: 22px;
  font-weight: 700;
  line-height: 150%; /* 33px */
  letter-spacing: -0.44px;
  margin-bottom: 20px;
`;

const Dt = styled.dt`
  color: var(--KTP-Grayscale-50, var(--KTP-Color-Mono-50, #80848a));
  font-size: 12px;
  font-weight: 400;
  line-height: 18px; /* 150% */
  letter-spacing: -0.24px;
`;

const Dd = styled.dd`
  color: var(--KTP-Grayscale-100, var(--KTP-Color-Mono-100, #030303));
  font-size: 14px;
  font-weight: 500;
  line-height: 150%; /* 21px */
  letter-spacing: -0.28px;
`;

const InfoList = styled.dl`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CompletedImage = styled.img`
  position: absolute;
  bottom: 0;
  right: -16px;
  width: 125px;
`;

const InfoItem = styled.div``;

const Barcode = styled.div`
  width: 100%;
  margin-top: 20px;
`;

const BarcodeImage = styled(ReactBarcode)`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const ReceiptDetailButton = styled.button`
  width: 100%;
  padding: 14px 24px;
  border-radius: 8px;
  background: #246cf6;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.25);
  color: #fff;
  text-align: center;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  margin-top: 8px;
`;
